import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import TableRemoveIcon from 'mdi-react/TableRemoveIcon';
import TableSearchIcon from 'mdi-react/TableSearchIcon';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  SimpleGrid,
  Checkbox,
  Select,
  Tooltip,
  Icon,
  InputGroup,
  InputRightAddon,
  IconButton,
  Flex,
  Text,
  List,
  ListItem,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import OwnSelect, { SelectedValues, SelectedValue } from 'components/OwnSelect';

import useAlert from 'hooks/useAlert';
import useModal from 'hooks/useModal';

import { State } from 'types/address';
import { Locale } from 'types/base';
import { UnitCost } from 'types/unit-cost';

type FormErrors = {
  [key: string]: string | boolean;
};

interface ModalBaseAddProps extends ModalRootProps {
  data: {
    base_id?: number;
    description?: string;
    model_type?: ModelType;
    use_social_law?: boolean;
    unit_cost?: UnitCost;
  };
}

type ModelType = 'sinapi' | 'sienge';
type PriceType = 'taxed,untaxed' | 'default';

function isArray(v: SelectedValue | SelectedValues): v is SelectedValues {
  return Array.isArray(v);
}

const ModalBaseAdd: React.FC<ModalBaseAddProps> = ({
  onConfirm,
  handleClose,
  data = {},
  mode,
  ...restProps
}) => {
  const { openAlert } = useAlert();
  const { openModal } = useModal();

  const [description, setDescription] = useState<string>(
    data.description || '',
  );

  const [modelType, setModelType] = useState<ModelType>(
    data.model_type || 'sinapi',
  );

  const modelTypes = useMemo(
    () => [
      { value: 'sinapi', label: 'SINAPI' },
      { value: 'sienge', label: 'Sienge' },
    ],
    [],
  );

  const [unitCost, setUnitCost] = useState<UnitCost>(
    data.unit_cost ? data.unit_cost : ({} as UnitCost),
  );

  const isUnitCostSelected = Object.keys(unitCost).length > 0;

  const descriptionRef = React.useRef<HTMLInputElement>(null);
  const [useSocialLaw, setUseSocialLaw] = useState(
    data.use_social_law || false,
  );

  const [isAllStates, setIsAllStates] = useState(false);
  const [stateSelected, setStateSelected] = useState<number[]>([]);
  const [localeList, setLocaleList] = useState<Locale[]>([]);
  const [stateList, setStateList] = useState<State[]>([]);

  const lockStateMultiSelect = useMemo(() => {
    return modelType === 'sienge';
  }, [modelType]);

  const isEditingSiengeBase = mode === 'edit' && modelType === 'sienge';

  const [selectedPriceType, setSelectedPriceType] =
    useState<PriceType>('default');

  const [loading, setLoading] = useState(false);
  const [isFindingStates, setIsFindingStates] = useState(false);
  const [isFindingLocales, setIsFindingLocales] = useState(false);
  const [, setIsFindingPriceTypes] = useState(false);

  const [errors, setErrors] = useState<FormErrors | undefined>();

  useEffect(() => {
    if (descriptionRef.current) {
      descriptionRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (modelType === 'sienge' && stateSelected.length > 1) {
      const [first] = stateSelected;
      setStateSelected([first]);
    }
  }, [modelType, stateSelected]);

  const statesToSelect = useMemo(
    () =>
      stateList.map((state) => ({
        value: state.id,
        label: state.name,
      })),
    [stateList],
  );

  const getStates = useCallback(async () => {
    setIsFindingStates(true);
    try {
      const { data: responseStates } = await api.get(`/state`);
      const states = responseStates.data;
      setStateList(states);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar a lista de estados.',
        status: 'error',
      });
    } finally {
      setIsFindingStates(false);
    }
  }, []);

  useEffect(() => {
    getStates();
  }, [getStates]);

  const getLocales = useCallback(async () => {
    setIsFindingLocales(true);
    try {
      const { data: responseLocales } = await api.get('/locale', {
        params: {
          'filter[status]': 'active',
          'filter[base_id]': data.base_id,
          sort: 'description',
        },
      });
      const locales = responseLocales.data;
      setLocaleList(locales);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar a lista de estados.',
        status: 'error',
      });
    } finally {
      setIsFindingLocales(false);
    }
  }, [data]);

  useEffect(() => {
    if (data.base_id) {
      getLocales();
    }
  }, [getLocales, data]);

  const getPriceTypes = useCallback(async () => {
    setIsFindingPriceTypes(true);
    try {
      const { data: responsePriceTypes } = await api.get('/price/type', {
        params: {
          'filter[status]': 'active',
          'filter[base_id]': data.base_id,
          sort: 'description',
        },
      });
      const locales = responsePriceTypes.data;
      if (locales.length === 2) {
        setSelectedPriceType('taxed,untaxed');
      } else {
        setSelectedPriceType('default');
      }
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar a lista de estados.',
        status: 'error',
      });
    } finally {
      setIsFindingPriceTypes(false);
    }
  }, [data]);

  useEffect(() => {
    if (data.base_id) {
      getPriceTypes();
    }
  }, [getPriceTypes, data]);

  useEffect(() => {
    if (localeList.length > 0 && stateList.length > 0) {
      const localesIds = stateList
        .filter((state) =>
          localeList.some((locale) => locale.key === state.abbreviation),
        )
        .map((state) => state.id);
      setStateSelected(localesIds);
    }
  }, [localeList, stateList]);

  const getStatesNameById = useCallback(
    (stateId: number[]) => {
      const states = stateList.filter((state) => stateId.includes(state.id));
      return states.map((state) => ({
        key: state.abbreviation,
        description: state.name,
      }));
    },
    [stateList],
  );

  useEffect(() => {
    if (stateSelected.length === statesToSelect.length) {
      setIsAllStates(true);
    } else {
      setIsAllStates(false);
    }
  }, [stateSelected, statesToSelect]);

  const handleConfirm = async (skipSiengeAlert?: boolean): Promise<void> => {
    const formErrors: FormErrors = {} as FormErrors;

    if (modelType === 'sienge' && !isUnitCostSelected) {
      Object.assign(formErrors, { unitCost: true });
    }

    if (stateSelected.length === 0) {
      Object.assign(formErrors, { states: true });
    }

    if (modelType === 'sienge' && stateSelected.length > 1) {
      Object.assign(formErrors, { states: true });
    }

    if (!description) {
      Object.assign(formErrors, { description: true });
    }

    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      return;
    }

    if (mode === 'add' && modelType === 'sienge' && !skipSiengeAlert) {
      openAlert({
        size: 'lg',
        type: 'warning',
        title: 'Criação de base do tipo Sienge',
        description: () => (
          <Flex direction="column" gap={3}>
            <Text>
              Ao criar uma base com o{' '}
              <Text as="span" fontWeight="bold">
                tipo Sienge
              </Text>
              , algumas informações não poderão ser alteradas depois.
            </Text>
            <Text fontWeight="bold">
              Depois da criação, não será possível editar:
            </Text>
            <List pl={4} spacing={1} styleType="disc">
              <ListItem>Tabela do Sienge Plataforma</ListItem>
              <ListItem>Estado</ListItem>
            </List>
            <Text fontWeight="bold">Deseja continuar?</Text>
          </Flex>
        ),
        buttons: ['Cancelar', 'Continuar'],
        onConfirm: () => handleConfirm(true),
      });

      return;
    }

    try {
      setLoading(true);

      if (mode === 'add') {
        const isSiengeBase = modelType === 'sienge' && isUnitCostSelected;

        let unitCostId: number | null = null;

        if (isSiengeBase) {
          const {
            data: {
              data: { id: newId },
            },
          } = await api.post(`/unit/cost`, {
            description: unitCost.description,
            external_id: unitCost.external_id,
            external_status: unitCost.external_status,
            enable_for_integration: unitCost.enable_for_integration,
            is_reference: unitCost.is_reference,
            is_default: unitCost.is_default,
          });

          unitCostId = newId;
        }

        const priceTypes = (() => {
          const defaultPrice = [{ key: 'default', description: 'Padrão' }];
          const taxedOrUntaxedPrice = [
            {
              key: 'taxed',
              description: 'Não Desonerado',
            },
            {
              key: 'untaxed',
              description: 'Desonerado',
            },
          ];

          if (isSiengeBase) {
            return defaultPrice;
          }

          return selectedPriceType === 'default'
            ? defaultPrice
            : taxedOrUntaxedPrice;
        })();

        const { data: newBase } = await api.post(`/base`, {
          key: 'own',
          description,
          model_type: modelType,
          use_social_law: isSiengeBase ? false : useSocialLaw,
          ...(isSiengeBase &&
            unitCostId && {
              integration_code: unitCost.external_id,
              unit_cost_id: unitCostId,
            }),
          locales: getStatesNameById(stateSelected),
          price_types: priceTypes,
        });

        const newBaseId = newBase.data.id;

        if (!isSiengeBase) {
          await api.post(`/version`, {
            base_id: newBaseId,
            description: 'Ref. Padrão',
          });
        }
      } else if (mode === 'edit') {
        await api.put(`/base/${data.base_id}`, {
          key: 'own',
          description,
          model_type: modelType,
          use_social_law: useSocialLaw,
          locales: getStatesNameById(stateSelected),
        });
      }

      if (onConfirm) onConfirm();

      toast({
        description:
          mode === 'add'
            ? 'Base adicionada com sucesso!'
            : 'Base editada com sucesso!',
        status: 'success',
      });

      handleClose();
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          (mode === 'add'
            ? 'Houve um erro ao adicionar a base!'
            : 'Houve um erro ao editar a base!'),
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal {...restProps} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === 'add' ? 'Adicionar base' : 'Editar base'}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl marginTop="4" isInvalid={!!errors?.description}>
              <FormLabel>Descrição</FormLabel>

              <Input
                ref={descriptionRef}
                type="text"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (e.target.value && errors?.description) {
                    setErrors({ ...errors, description: false });
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
              />

              <FormErrorMessage>
                {!!errors?.description && 'Descrição é obrigatória'}
              </FormErrorMessage>
            </FormControl>

            <FormControl marginTop="4">
              <FormLabel>Modelo</FormLabel>

              <OwnSelect
                placeholder="Selecione"
                options={modelTypes}
                value={modelTypes.find((mt) => mt.value === modelType) || null}
                isClearable={false}
                isDisabled={mode === 'edit'}
                onChange={(selected: SelectedValue) => {
                  if (selected) {
                    setModelType(String(selected.value) as ModelType);
                  }
                }}
              />

              <FormErrorMessage>
                {!!errors?.modelType && 'Modelo é obrigatório'}
              </FormErrorMessage>
            </FormControl>
          </SimpleGrid>

          {modelType === 'sienge' && (
            <FormControl isInvalid={!!errors?.unitCost} mt={4}>
              <FormLabel>Tabela do Sienge Plataforma</FormLabel>

              <InputGroup>
                <Input
                  type="text"
                  mb={{ base: 3, md: 0 }}
                  borderTopRightRadius={mode === 'add' ? '0' : 'md'}
                  borderBottomRightRadius={mode === 'add' ? '0' : 'md'}
                  placeholder="Selecione a tabela do Sienge Plataforma"
                  readOnly
                  isReadOnly
                  _readOnly={{
                    background: mode === 'add' ? 'white' : 'gray.100',
                  }}
                  value={
                    isUnitCostSelected
                      ? `${unitCost.external_id} - ${unitCost.description}`
                      : ''
                  }
                />

                {mode === 'add' && (
                  <>
                    <InputRightAddon px={0}>
                      <IconButton
                        height="100%"
                        colorScheme="green"
                        aria-label="Selecionar obra"
                        title="Selecionar obra"
                        borderRadius={!isUnitCostSelected ? 'base' : '0'}
                        borderTopLeftRadius="0"
                        borderBottomLeftRadius="0"
                        width="50px"
                        onClick={() => {
                          openModal('unitCostLookup', {
                            mode: 'lookup',
                            onConfirm: (selected) => {
                              setUnitCost(selected);
                              setErrors({
                                ...errors,
                                unitCost: false,
                              });
                            },
                          });
                        }}
                      >
                        <Icon as={TableSearchIcon} w="20px" h="20px" />
                      </IconButton>
                    </InputRightAddon>

                    {isUnitCostSelected && (
                      <InputRightAddon px={0}>
                        <IconButton
                          height="100%"
                          colorScheme="red"
                          aria-label="Remover obra orçamento"
                          title="Remover obra orçamento"
                          borderTopLeftRadius="0"
                          borderBottomLeftRadius="0"
                          width="50px"
                          onClick={() => {
                            setUnitCost({} as UnitCost);
                          }}
                        >
                          <Icon as={TableRemoveIcon} w="20px" h="20px" />
                        </IconButton>
                      </InputRightAddon>
                    )}
                  </>
                )}
              </InputGroup>

              <FormErrorMessage>
                {!!errors?.unitCost && 'Tabela do Sienge é obrigatório'}
              </FormErrorMessage>
            </FormControl>
          )}

          <FormControl isInvalid={!!errors?.states} mt={4}>
            <FormLabel>
              <span>Estado</span>
              {modelType !== 'sienge' && (
                <>
                  <Checkbox
                    mt={1}
                    ml={5}
                    mr={1}
                    isChecked={isAllStates}
                    onChange={(e) => {
                      setIsAllStates(e.target.checked);
                      if (e.target.checked) {
                        setStateSelected(
                          statesToSelect.map((state) => state.value),
                        );

                        if (errors?.states) {
                          setErrors({ ...errors, states: false });
                        }
                      }
                    }}
                    size="sm"
                  />
                  Todos os estados
                </>
              )}
            </FormLabel>

            <OwnSelect
              placeholder="Selecione"
              options={statesToSelect}
              isMulti={!lockStateMultiSelect}
              value={
                lockStateMultiSelect
                  ? statesToSelect.find((s) => s.value === stateSelected[0]) ||
                    null
                  : statesToSelect.filter((s) =>
                      stateSelected.includes(s.value),
                    )
              }
              isInvalid={!!errors?.states}
              isLoading={isFindingStates || isFindingLocales}
              isDisabled={
                isEditingSiengeBase ||
                isFindingStates ||
                isFindingLocales ||
                statesToSelect.length === 0
              }
              isClearable
              onChange={(selected: SelectedValue | SelectedValues) => {
                let value: number[] = [];

                if (lockStateMultiSelect && !isArray(selected)) {
                  value = selected ? [Number(selected.value)] : [];
                } else if (isArray(selected)) {
                  value = selected ? selected.map((s) => Number(s.value)) : [];
                }

                setStateSelected(value);

                if (value.length > 0 && errors?.states) {
                  setErrors({ ...errors, states: false });
                }
              }}
            />

            <FormErrorMessage>Estado é obrigatório</FormErrorMessage>
          </FormControl>

          {modelType !== 'sienge' && (
            <>
              <FormControl mt={4}>
                <FormLabel>Tipo de preço</FormLabel>

                <Select
                  value={selectedPriceType}
                  onChange={(e) =>
                    setSelectedPriceType(e.target.value as PriceType)
                  }
                  isDisabled={mode === 'edit'}
                  _disabled={{
                    background: 'hsl(0, 0%, 95%)',
                    border: '1px solid hsl(0, 0%, 90%)',
                  }}
                >
                  <option value="variable">Padrão</option>
                  <option value="taxed,untaxed">
                    Desonerado / Não desonerado
                  </option>
                </Select>
                <FormErrorMessage>Estado é obrigatório</FormErrorMessage>
              </FormControl>

              <Checkbox
                mt={5}
                mr={1}
                isChecked={useSocialLaw}
                onChange={(e) => setUseSocialLaw(e.target.checked)}
              >
                Campo de leis sociais (%){' '}
                <Tooltip
                  hasArrow
                  label="Marque esta opção caso deseja modificar o % (percentual) das Leis Sociais na configuração do Orçamento."
                >
                  <Icon
                    as={InfoCircleFillIcon}
                    className="icon"
                    color="blue.300"
                  />
                </Tooltip>
              </Checkbox>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={() => handleConfirm()}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBaseAdd;
